import invariant from 'tiny-invariant'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Text,
  Badge,
  Button,
  ErrorMessage,
  InfoBox,
  InfoRow,
  Primary14ExtendedSemiBold,
  PrimaryPrimary80,
  RedRed60,
  SimpleModal,
  usePersistMagicTableQueryParams,
  Box,
  Flex
} from '@sequencehq/core-components'
import JSONPretty from 'react-json-pretty'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useState } from 'react'
import { DuplicateUsageEventDrawer } from './DuplicateUsageEventDrawer'
import { ProtectedButton } from 'lib/rbac'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { useUnmappedAliasResolution } from 'Customer/index'

export const ViewUsageEvent = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const { persistMagicTableQueryParams } = usePersistMagicTableQueryParams()
  const [isDuplicateDrawerOpen, setIsDuplicateDrawerOpen] = useState(false)

  invariant(eventId, 'eventId is required')

  const usageEventQuery = useQuery(dashboard20240730Client.getUsageEvent, {
    id: eventId
  })

  const handleClose = () => {
    navigate(persistMagicTableQueryParams('/usage-events'))
  }

  const handleDuplicateClose = () => {
    navigate(persistMagicTableQueryParams('/usage-events'))
  }

  const usageEvent = usageEventQuery.data

  const customerQuery = useQuery(
    dashboard20240730Client.getCustomerByAlias,
    usageEvent ? { alias: usageEvent.customerAlias } : undefined,
    // A 404 is expected when the alias hasn't been mapped to a customer yet, so
    // don't retry — we surface the linking actions instead.
    { retry: false }
  )

  const isAliasUnmapped = Boolean(
    usageEvent?.customerAlias && !customerQuery.isPending && !customerQuery.data
  )

  const { banner, linkButtons, linkModals } = useUnmappedAliasResolution({
    alias: usageEvent?.customerAlias ?? '',
    testIdPrefix: 'usageEvent'
  })

  if (isDuplicateDrawerOpen && usageEvent) {
    return (
      <DuplicateUsageEventDrawer
        isOpen={true}
        onClose={handleDuplicateClose}
        usageEvent={usageEvent}
      />
    )
  }

  return (
    <SimpleModal
      isOpen={true}
      variant="drawer"
      title={usageEvent?.id || 'Usage Event'}
      cancelButtonText="Close"
      onClose={handleClose}
      showSubmitButton={false}
      isLoading={usageEventQuery.isPending}
      data-testid="viewUsageEvent.drawer"
      closeOnUpdate={false}
    >
      {!usageEvent ? (
        <Flex alignItems="center" justifyContent="center" height="100%">
          <ErrorMessage />
        </Flex>
      ) : (
        <Flex direction="column" gap="20px" height="100%" overflow="hidden">
          {isAliasUnmapped && <Box flexShrink={0}>{banner}</Box>}
          <Box flexShrink={0}>
            <Text {...Primary14ExtendedSemiBold} marginBottom="8px">
              Details
            </Text>
            <InfoBox data-testid="usageEvent.info">
              <InfoRow label="Event ID">{usageEvent.customerEventId}</InfoRow>
              <InfoRow label="Date">{usageEvent.eventTimestamp}</InfoRow>
              <InfoRow label="Event type">
                <Badge sentiment="monospaced" textTransform="none">
                  {usageEvent.eventType}
                </Badge>
              </InfoRow>
              {customerQuery.data && (
                <InfoRow label="Customer">
                  <CustomerPreviewCardPill
                    customerId={customerQuery.data.id}
                    variant="ghost"
                    data-testid="usageEvent.inspector.customer"
                  />
                </InfoRow>
              )}
              <InfoRow label="Customer alias">
                {usageEvent.customerAlias}
              </InfoRow>
            </InfoBox>
          </Box>

          <Flex flexShrink={0} direction="column" gap="8px">
            {isAliasUnmapped && linkButtons}
            <ProtectedButton permission="org.usage:update">
              <Button
                variant="secondary"
                onClick={() => setIsDuplicateDrawerOpen(true)}
                data-testid="usageEvent.duplicate"
                fullWidth
              >
                Overwrite event
              </Button>
            </ProtectedButton>
          </Flex>

          <Box>
            <Text
              {...Primary14ExtendedSemiBold}
              marginBottom="8px"
              flexShrink={0}
            >
              Data
            </Text>
            <InfoBox
              data-testid="usageEvent.data"
              overflow="auto"
              padding="8px"
            >
              <JSONPretty
                id="json-pretty"
                data={usageEvent}
                theme={{
                  main: 'font-size: 14px; line-height: 20px;',
                  key: `color:${PrimaryPrimary80};`,
                  string: `color:${RedRed60};`
                }}
              />
            </InfoBox>
          </Box>

          {linkModals}
        </Flex>
      )}
    </SimpleModal>
  )
}
